import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

function formatDateTime(val) {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).replace(",", "");
}

function workerIdFromTicket(t) {
  if (!t?.assignedWorkerId) return "";
  const w = t.assignedWorkerId;
  if (typeof w === "object" && w !== null && w._id != null) return String(w._id);
  return String(w);
}

function normalizePhotoUrls(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

function ticketSourceLabel(value) {
  const v = String(value || "").toLowerCase();
  if (v === "web") return "Web";
  if (v === "app") return "App";
  return "-";
}

function severityBadge(severity) {
  const s = String(severity || "").toLowerCase();
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "84px",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    border: "1px solid transparent",
  };
  if (s === "critical") return { ...base, background: "#fee2e2", color: "#991b1b", borderColor: "#fecaca" };
  if (s === "high") return { ...base, background: "#8b1634", color: "#fff", borderColor: "#7a102d", boxShadow: "0 1px 6px rgba(139,22,52,0.35)" };
  if (s === "medium") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fde68a" };
  if (s === "low") return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#bbf7d0" };
  return { ...base, background: "#e2e8f0", color: "#334155", borderColor: "#cbd5e1" };
}

function photoSrcForDisplay(value) {
  if (value == null || typeof value !== "string") return "";
  const s = value.trim();
  if (!s) return "";
  if (s.startsWith("data:")) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith("//") || s.startsWith("blob:")) return s;
  // Server-hosted file (e.g. "/uploads/assets/x.jpg") — prefix the backend host.
  if (s.startsWith("/uploads/")) {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
    return `${baseUrl}${s}`;
  }
  const compact = s.replace(/\s/g, "");
  if (compact.length < 80 || !/^[A-Za-z0-9+/]+=*$/.test(compact)) return s;
  if (compact.startsWith("iVBORw0KGgo") || compact.startsWith("iVBOR")) return `data:image/png;base64,${compact}`;
  if (compact.startsWith("R0lGOD")) return `data:image/gif;base64,${compact}`;
  if (compact.startsWith("UklGR")) return `data:image/webp;base64,${compact}`;
  if (compact.startsWith("/9j")) return `data:image/jpeg;base64,${compact}`;
  return `data:image/jpeg;base64,${compact}`;
}

function getRoleFromStorage() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return String(user?.role || "").toLowerCase();
  } catch {
    return "";
  }
}

export default function AssetTicketsPage() {
  const [items, setItems] = useState([]);
  const [labelCodeOptions, setLabelCodeOptions] = useState([]);
  const [showLabelCodeDropdown, setShowLabelCodeDropdown] = useState(false);
  const [showIssueTypeDropdown, setShowIssueTypeDropdown] = useState(false);
  const [showSeverityDropdown, setShowSeverityDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [appUsers, setAppUsers] = useState([]);
  const [assignWorkerId, setAssignWorkerId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createBySite, setCreateBySite] = useState(false);
  const [createSiteFilter, setCreateSiteFilter] = useState("");
  const [createForm, setCreateForm] = useState({
    labelCode: "",
    issueType: "fault",
    severity: "high",
    description: "",
  });
  const ticketStatusMapRef = useRef(new Map());
  const hasInitialTicketSnapshotRef = useRef(false);
  const userRole = getRoleFromStorage();
  const canCreateTicket = userRole === "admin" || userRole === "subadmin";
  const canAssignTicket = userRole === "admin" || userRole === "subadmin";

  useEffect(() => {
    const loadTickets = async (showLoader = true) => {
      if (showLoader) setLoading(true);
      setError("");
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(`${baseUrl}/api/asset/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || !data?.success) {
          setItems([]);
          setError(data?.message || "Failed to load tickets");
          return;
        }
        const nextItems = Array.isArray(data.data) ? data.data : [];

        if (hasInitialTicketSnapshotRef.current) {
          const justClosed = nextItems.filter((t) => {
            const id = String(t?._id || "");
            if (!id) return false;
            const prevStatus = String(ticketStatusMapRef.current.get(id) || "").toLowerCase();
            const nextStatus = String(t?.status || "").toLowerCase();
            return prevStatus && prevStatus !== "closed" && nextStatus === "closed";
          });
          if (justClosed.length) {
            justClosed.forEach((t) => {
              toast.success(`Ticket ${t.ticketNumber || ""} closed automatically because linked asset was retired.`);
            });
          }
        }

        const nextMap = new Map();
        nextItems.forEach((t) => {
          if (t?._id) nextMap.set(String(t._id), String(t.status || ""));
        });
        ticketStatusMapRef.current = nextMap;
        hasInitialTicketSnapshotRef.current = true;
        setItems(nextItems);
      } catch {
        setItems([]);
        setError("Failed to load tickets");
      } finally {
        if (showLoader) setLoading(false);
      }
    };

    const loadUsers = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const usersRes = await fetch(`${baseUrl}/api/app-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const usersData = await usersRes.json();
        setAppUsers(usersRes.ok && usersData?.success && Array.isArray(usersData?.data) ? usersData.data : []);
      } catch {
        setAppUsers([]);
      }
    };

    const loadLabelCodes = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const [assetRes, labelRes] = await Promise.all([
          fetch(`${baseUrl}/api/asset`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${baseUrl}/api/asset-management/labels`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const [assetData, labelData] = await Promise.all([assetRes.json(), labelRes.json()]);
        const normalizeLabelCode = (value) => String(value || "").trim().toLowerCase();
        const fromLabels =
          labelRes.ok && labelData?.success && Array.isArray(labelData?.data)
            ? labelData.data
                .filter((x) => String(x?.status || "").toLowerCase() === "assigned")
                .map((x) => ({
                  labelCode: String(x?.labelCode || "").trim(),
                  labelCodeKey: normalizeLabelCode(x?.labelCode),
                  siteName: "",
                  assetName: "",
                }))
                .filter((x) => x.labelCodeKey)
            : [];
        const fromAssets =
          assetRes.ok && assetData?.success && Array.isArray(assetData?.data)
            ? assetData.data
                .filter((x) => String(x?.assetStatus || "").toLowerCase() !== "retired")
                .map((x) => ({
                  labelCode: String(x?.labelCode || "").trim(),
                  labelCodeKey: normalizeLabelCode(x?.labelCode),
                  siteName: String(x?.siteName || "").trim(),
                  assetName: String(x?.deviceName || "").trim(),
                }))
                .filter((x) => x.labelCodeKey)
            : [];
        const map = new Map();
        [...fromLabels, ...fromAssets].forEach((entry) => {
          const key = entry.labelCodeKey;
          if (!key) return;
          const prev = map.get(key);
          if (!prev) {
            map.set(key, entry);
            return;
          }
          if (!prev.siteName && entry.siteName) {
            map.set(key, entry);
            return;
          }
          if (!prev.assetName && entry.assetName) {
            map.set(key, entry);
          }
        });
        const all = Array.from(map.values()).sort((a, b) => a.labelCode.localeCompare(b.labelCode));
        setLabelCodeOptions(all);
      } catch {
        setLabelCodeOptions([]);
      }
    };

    loadTickets(true);
    loadUsers();
    loadLabelCodes();
    const intervalId = setInterval(() => {
      loadTickets(false);
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);

  const siteNameByLabelCode = new Map(
    labelCodeOptions.map((entry) => [String(entry?.labelCode || "").trim().toLowerCase(), String(entry?.siteName || "").trim()])
  );

  const getTicketSiteName = (row) => {
    const direct = String(row?.siteName || row?.site || "").trim();
    if (direct) return direct;
    const fromLabel = siteNameByLabelCode.get(String(row?.labelCode || "").trim().toLowerCase());
    return String(fromLabel || "").trim();
  };

  const filtered = items.filter((row) => {
    if (statusFilter && String(row.status || "") !== statusFilter) return false;
    if (severityFilter && String(row.severity || "").toLowerCase() !== severityFilter.toLowerCase()) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [
      row.date,
      row.ticketNumber,
      row.labelCode,
      getTicketSiteName(row),
      row.serialNumber,
      row.location,
      row.issueType,
      row.priority,
      row.severity,
      row.status,
      row.description,
    ]
      .map((v) => String(v || "").toLowerCase())
      .join(" ")
      .includes(q);
  });
  const labelCodeQuery = String(createForm.labelCode || "").trim().toLowerCase();
  const filteredLabelCodeOptions = labelCodeOptions
    .filter((entry) => {
      if (createBySite && createSiteFilter && String(entry.siteName || "") !== createSiteFilter) return false;
      const text = `${entry.labelCode} ${entry.assetName}`.toLowerCase();
      return text.includes(labelCodeQuery);
    })
    .slice(0, 40);
  const siteNameOptions = [...new Set(labelCodeOptions.map((x) => String(x.siteName || "").trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
  const issueTypeOptions = [
    { value: "fault", label: "Fault" },
    { value: "maintenance", label: "Maintenance" },
  ];
  const severityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  const handleAssign = async () => {
    if (!selectedTicket?._id) return;
    if (!canAssignTicket) {
      toast.error("Only Admin/SubAdmin can assign work to field team.");
      return;
    }
    if (!assignWorkerId) return;
    const assigningToastId = toast.info("Assigning work to field team...", { autoClose: 1500 });
    setAssignLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/asset/tickets/${selectedTicket._id}/assign`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ assignedWorkerId: assignWorkerId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.dismiss(assigningToastId);
        toast.error(data?.message || "Failed to assign worker");
        setError(data?.message || "Failed to assign worker");
        setAssignLoading(false);
        return;
      }
      const updated = data.data;
      setItems((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
      setSelectedTicket(updated);
      setAssignWorkerId(workerIdFromTicket(updated));
      setActionMessage("Worker assigned successfully.");
      toast.dismiss(assigningToastId);
      toast.success("Work assigned successfully.");
    } catch {
      toast.dismiss(assigningToastId);
      toast.error("Failed to assign work.");
      setError("Failed to assign worker");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!createForm.labelCode.trim()) {
      toast.error("Label code is required");
      return;
    }
    setCreateLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/asset/tickets/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          labelCode: createForm.labelCode.trim(),
          issueType: createForm.issueType,
          severity: createForm.severity,
          description: createForm.description.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to create ticket");
        return;
      }
      const created = data.data;
      if (created) setItems((prev) => [created, ...prev]);
      toast.success("Ticket created successfully.");
      setShowCreateModal(false);
      setCreateForm({ labelCode: "", issueType: "fault", severity: "high", description: "" });
      setCreateBySite(false);
      setCreateSiteFilter("");
    } catch {
      toast.error("Failed to create ticket");
    } finally {
      setCreateLoading(false);
    }
  };

  const selectedAssignedId = selectedTicket ? workerIdFromTicket(selectedTicket) : "";
  const assigneeModalObj =
    selectedTicket?.assignedWorkerId && typeof selectedTicket.assignedWorkerId === "object"
      ? selectedTicket.assignedWorkerId
      : null;
  const assigneeInAppUsersList =
    Boolean(selectedAssignedId) && appUsers.some((u) => String(u._id) === selectedAssignedId);
  const assignmentSectionLocked =
    Boolean(selectedAssignedId) || selectedTicket?.status === "closed";

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 700, color: "#1a202c", margin: 0, whiteSpace: "nowrap" }}>Asset Fault & Maint. Tickets</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(139,22,52,0.45)",
              fontSize: "14px",
              minWidth: "180px",
              color: "#8b1634",
              fontWeight: 600,
              background: "#fff",
            }}
          >
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(139,22,52,0.45)",
              fontSize: "14px",
              minWidth: "160px",
              color: "#8b1634",
              fontWeight: 600,
              background: "#fff",
            }}
          >
            <option value="">All severity</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <input
            type="text"
            placeholder="Search tickets"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(139,22,52,0.35)",
              fontSize: "14px",
              minWidth: "190px",
            }}
          />
          {(statusFilter || severityFilter || search) ? (
            <button
              type="button"
              onClick={() => {
                setStatusFilter("");
                setSeverityFilter("");
                setSearch("");
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "none",
                background: "#e53e3e",
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          ) : null}
          {canCreateTicket ? (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "none",
                background: "#8b1634",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
              }}
            >
              + Add Ticket
            </button>
          ) : null}
        </div>
      </div>

      {error ? <div style={{ padding: "8px 0 16px", color: "#e53e3e" }}>{error}</div> : null}

      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "12px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1420px" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={th}>Ticket No</th>
              <th style={{ ...th, minWidth: "150px" }}>Date</th>
              <th style={th}>Label Code</th>
              <th style={th}>Site Name</th>
              <th style={th}>Serial Number</th>
              <th style={th}>Location</th>
              <th style={th}>Issue Type</th>
              <th style={th}>Severity</th>
              <th style={{ ...th, minWidth: "110px" }}>Status</th>
              <th style={th}>Assigned Worker</th>
              <th style={th}>Created From</th>
              <th style={th}>Description</th>
              <th style={{ ...th, minWidth: "150px" }}>Created At</th>
              <th style={th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: "#718096", padding: "28px 12px" }} colSpan={14}>
                  Loading tickets...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: "#718096" }} colSpan={14}>
                  No tickets found.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr
                  key={row._id}
                  style={
                    String(row.severity || "").toLowerCase() === "high"
                      ? { background: "#fff1f2" }
                      : undefined
                  }
                >
                  <td style={td}>{row.ticketNumber || "-"}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{formatDateTime(row.date || row.createdAt)}</td>
                  <td style={td}>{row.labelCode || "-"}</td>
                  <td style={td}>{getTicketSiteName(row) || "-"}</td>
                  <td style={td}>{row.serialNumber || "-"}</td>
                  <td style={td}>{row.location || "-"}</td>
                  <td style={{ ...td, textTransform: "capitalize" }}>{row.issueType || "-"}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <span style={severityBadge(row.severity)}>{String(row.severity || "-")}</span>
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <span style={statusBadge(row.status)}>{String(row.status || "-").replace("_", " ")}</span>
                  </td>
                  <td style={td}>
                    {row.assignedWorkerId
                      ? `${row.assignedWorkerId.firstName || ""} ${row.assignedWorkerId.lastName || ""}`.trim() || row.assignedWorkerId.email || "-"
                      : "-"}
                  </td>
                  <td style={td}>{ticketSourceLabel(row.ticketSource)}</td>
                  <td style={td}>{row.description || "-"}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{formatDateTime(row.createdAt)}</td>
                  <td style={td}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTicket(row);
                        setAssignWorkerId(workerIdFromTicket(row));
                        setActionMessage("");
                      }}
                      style={{
                        border: "none",
                        background: "#8b1634",
                        color: "#fff",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedTicket ? (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "16px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "none", overflow: "hidden" }}>
              <div
                className="modal-header"
                style={{
                  borderBottom: "none",
                  background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)",
                  padding: "14px 18px",
                }}
              >
                <h5 className="modal-title" style={{ fontWeight: 700, color: "#fff", fontSize: "18px" }}>
                  Ticket Details
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setSelectedTicket(null)} aria-label="Close" style={{ opacity: 0.9 }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "80vh", overflowY: "auto", padding: "20px", background: "#f1f5f9" }}>
                {selectedTicket.status === "closed" ? (
                  <div style={{ marginBottom: "14px", padding: "12px 14px", borderRadius: "10px", background: "#ecfdf3", color: "#166534", fontWeight: 600, fontSize: "13px", border: "1px solid #bbf7d0" }}>
                    This ticket is closed and work is completed.
                  </div>
                ) : null}
                {actionMessage ? (
                  <div style={{ marginBottom: "14px", padding: "12px 14px", borderRadius: "10px", background: "#eff6ff", color: "#1e40af", fontWeight: 600, fontSize: "13px", border: "1px solid #bfdbfe" }}>
                    {actionMessage}
                  </div>
                ) : null}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                  <div style={{ ...panelStyle, borderLeft: "3px solid #8b1634", paddingLeft: "15px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "124px 1fr", rowGap: "10px", columnGap: "12px", fontSize: "13px", alignItems: "start" }}>
                      <div style={detailLabelStyle}>Ticket No</div><div style={detailValueStyle}>{selectedTicket.ticketNumber || "-"}</div>
                      <div style={detailLabelStyle}>Label Code</div><div style={{ ...detailValueStyle, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }}>{selectedTicket.labelCode || "-"}</div>
                      <div style={detailLabelStyle}>Date</div><div style={detailValueStyle}>{formatDateTime(selectedTicket.date || selectedTicket.createdAt)}</div>
                      <div style={detailLabelStyle}>Site Name</div><div style={detailValueStyle}>{getTicketSiteName(selectedTicket) || "-"}</div>
                      <div style={detailLabelStyle}>Serial Number</div><div style={detailValueStyle}>{selectedTicket.serialNumber || "-"}</div>
                      <div style={detailLabelStyle}>Location</div><div style={detailValueStyle}>{selectedTicket.location || "-"}</div>
                      <div style={detailLabelStyle}>Issue Type</div><div style={{ ...detailValueStyle, textTransform: "capitalize" }}>{selectedTicket.issueType || "-"}</div>
                      <div style={detailLabelStyle}>Severity</div><div style={{ ...detailValueStyle, textTransform: "capitalize" }}>{selectedTicket.severity || "-"}</div>
                      <div style={detailLabelStyle}>Status</div>
                      <div>
                        <span style={detailStatusBadge(selectedTicket.status)}>{(selectedTicket.status || "-").replace("_", " ")}</span>
                      </div>
                    </div>
                  </div>
                  <div style={panelStyle}>
                    <div style={{ ...sectionSubtitleStyle, marginBottom: "12px" }}>People & dates</div>
                    <div style={{ display: "grid", gridTemplateColumns: "124px 1fr", rowGap: "10px", columnGap: "12px", fontSize: "13px", alignItems: "start" }}>
                      <div style={detailLabelStyle}>Assigned to</div>
                      <div style={detailValueStyle}>
                        {selectedTicket.assignedWorkerId
                          ? `${selectedTicket.assignedWorkerId.firstName || ""} ${selectedTicket.assignedWorkerId.lastName || ""}`.trim() || selectedTicket.assignedWorkerId.email || "-"
                          : <span style={{ color: "#94a3b8" }}>Unassigned</span>}
                      </div>
                      <div style={detailLabelStyle}>Created</div><div style={detailValueStyle}>{formatDateTime(selectedTicket.createdAt)}</div>
                      <div style={detailLabelStyle}>Last updated</div><div style={detailValueStyle}>{formatDateTime(selectedTicket.updatedAt)}</div>
                    </div>
                  </div>
                </div>
                <div style={{ ...panelStyle, marginTop: "14px" }}>
                  <div style={{ ...sectionSubtitleStyle, marginBottom: "10px" }}>Description</div>
                  <div style={{ fontSize: "14px", lineHeight: 1.55, color: "#1e293b" }}>{selectedTicket.description || <span style={{ color: "#94a3b8" }}>No description provided.</span>}</div>
                  {normalizePhotoUrls(selectedTicket.photoUrl).length ? (
                    <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #e2e8f0" }}>
                      <div style={{ ...detailLabelStyle, marginBottom: "6px" }}>Attachment</div>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {normalizePhotoUrls(selectedTicket.photoUrl).map((photo, idx) => (
                          <button
                            key={`${idx}-${photo.slice(0, 20)}`}
                            type="button"
                            onClick={() => setPreviewImage(photo)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              border: "1px solid #cbd5e1",
                              background: "#fff",
                              color: "#8b1634",
                              borderRadius: "8px",
                              padding: "8px 14px",
                              fontSize: "13px",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            <i className="fa fa-image" style={{ fontSize: "14px", opacity: 0.85 }} />
                            {`View photo ${idx + 1}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                {canAssignTicket ? (
                  <div style={{ marginTop: "16px", padding: "16px", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,0.06)" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "13px", letterSpacing: "0.02em", marginBottom: "12px" }}>Assign work to field team</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "stretch" }}>
                      <select
                        value={assignWorkerId || selectedAssignedId || ""}
                        disabled={assignmentSectionLocked}
                        onChange={(e) => setAssignWorkerId(e.target.value)}
                        style={{
                          border: "1px solid #cbd5e1",
                          borderRadius: "8px",
                          padding: "10px 12px",
                          fontSize: "14px",
                          flex: "1 1 220px",
                          minWidth: "200px",
                          background: assignmentSectionLocked ? "#f8fafc" : "#fff",
                          color: "#0f172a",
                          cursor: assignmentSectionLocked ? "not-allowed" : "pointer",
                          opacity: assignmentSectionLocked ? 0.95 : 1,
                        }}
                      >
                        <option value="">Select assigned worker…</option>
                        {assigneeModalObj && !assigneeInAppUsersList && selectedAssignedId ? (
                          <option value={selectedAssignedId}>
                            {`${assigneeModalObj.firstName || ""} ${assigneeModalObj.lastName || ""}`.trim() || assigneeModalObj.email || "Assigned worker"}{" "}
                            {assigneeModalObj.email ? `(${assigneeModalObj.email})` : ""}
                          </option>
                        ) : null}
                        {appUsers.map((u) => (
                          <option key={u._id} value={u._id}>
                            {`${u.firstName || ""} ${u.lastName || ""}`.trim()} {u.email ? `(${u.email})` : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleAssign}
                        disabled={assignmentSectionLocked || !assignWorkerId || assignLoading}
                        style={{
                          border: "1px solid #8b1634",
                          background: "#fff",
                          color: "#8b1634",
                          borderRadius: "8px",
                          padding: "10px 18px",
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: assignmentSectionLocked || !assignWorkerId || assignLoading ? "not-allowed" : "pointer",
                          opacity: assignmentSectionLocked || !assignWorkerId || assignLoading ? 0.55 : 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {assignLoading ? "Assigning…" : "Assign"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "12px 18px", background: "#fff", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn"
                  style={{ border: "1px solid #8b1634", color: "#8b1634", background: "#fff", fontWeight: 600, borderRadius: "8px", padding: "8px 22px" }}
                  onClick={() => setSelectedTicket(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {previewImage ? (
        <div
          className="modal fade show"
          style={{
            display: "flex",
            position: "fixed",
            inset: 0,
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.55)",
            zIndex: 1080,
            padding: "16px",
          }}
          tabIndex="-1"
        >
          <div style={{ width: "520px", maxWidth: "100%" }}>
            <div className="modal-content" style={{ borderRadius: "14px", overflow: "hidden", border: "none", boxShadow: "0 25px 60px rgba(0,0,0,0.35)" }}>
              <div
                className="modal-header"
                style={{
                  borderBottom: "1px solid #e2e8f0",
                  background: "#ffffff",
                  padding: "12px 16px",
                }}
              >
                <h5 className="modal-title" style={{ margin: 0, fontWeight: 700, color: "#111827", fontSize: "16px" }}>
                  Fault Photo Preview
                </h5>
                <button type="button" className="btn-close" onClick={() => setPreviewImage("")} aria-label="Close" />
              </div>
              <div style={{ background: "#0f172a", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "320px", maxHeight: "60vh", overflow: "auto", padding: "12px" }}>
                <img
                  src={photoSrcForDisplay(previewImage)}
                  alt="Fault"
                  style={{ maxWidth: "100%", maxHeight: "52vh", objectFit: "contain", borderRadius: "10px", boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}
                />
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", background: "#fff", padding: "10px 16px" }}>
                <button
                  type="button"
                  className="btn"
                  style={{ border: "1px solid #8b1634", color: "#8b1634", background: "transparent", fontWeight: 600, borderRadius: "8px", padding: "7px 16px" }}
                  onClick={() => setPreviewImage("")}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showCreateModal ? (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "560px", width: "calc(100% - 2rem)" }}>
            <div className="modal-content border-0 shadow" style={{ borderRadius: "14px", overflow: "hidden" }}>
              <div className="modal-header border-0 py-3" style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", color: "#fff" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: "17px" }}>Create Ticket</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCreateModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body px-4 pt-3 pb-2">
                <div style={{ display: "grid", gap: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "2px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#475569", letterSpacing: "0.03em", textTransform: "uppercase" }}>
                      Create ticket by Label Code
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCreateBySite((prev) => !prev);
                        setCreateSiteFilter("");
                        setCreateForm((p) => ({ ...p, labelCode: "" }));
                        setShowLabelCodeDropdown(false);
                      }}
                      style={{
                        border: "1px solid #cbd5e1",
                        background: createBySite ? "#fdf2f8" : "#fff",
                        color: createBySite ? "#8b1634" : "#334155",
                        borderRadius: "999px",
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {createBySite ? "Using Site Name" : "Use Site Name"}
                    </button>
                  </div>
                  {createBySite ? (
                    <select
                      value={createSiteFilter}
                      onChange={(e) => {
                        setCreateSiteFilter(e.target.value);
                        setCreateForm((p) => ({ ...p, labelCode: "" }));
                        setShowLabelCodeDropdown(true);
                      }}
                      style={{
                        width: "100%",
                        border: "1px solid #dbe3ef",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        fontSize: "14px",
                        background: "#fff",
                        color: "#1e293b",
                        height: "42px",
                      }}
                    >
                      <option value="">Select site name</option>
                      {siteNameOptions.map((site) => (
                        <option key={site} value={site}>
                          {site}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder={createBySite ? "Search/select label code or asset name" : "Search or select label code"}
                      value={createForm.labelCode}
                      onFocus={() => setShowLabelCodeDropdown(true)}
                      onBlur={() => setTimeout(() => setShowLabelCodeDropdown(false), 120)}
                      onChange={(e) => {
                        setCreateForm((p) => ({ ...p, labelCode: e.target.value }));
                        setShowLabelCodeDropdown(true);
                      }}
                      style={{
                        border: createForm.labelCode ? "1px solid rgba(139,22,52,0.35)" : "1px solid #dbe3ef",
                        borderRadius: "10px",
                        padding: "10px 40px 10px 12px",
                        fontSize: "14px",
                        lineHeight: "20px",
                        height: "42px",
                        width: "100%",
                        background: createForm.labelCode ? "#fff7fa" : "#fff",
                        color: "#1e293b",
                      }}
                    />
                    {createForm.labelCode ? (
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setCreateForm((p) => ({ ...p, labelCode: "" }));
                          setShowLabelCodeDropdown(true);
                        }}
                        aria-label="Clear selected label code"
                        style={{
                          position: "absolute",
                          right: "9px",
                          top: "8px",
                          width: "24px",
                          height: "24px",
                          border: "none",
                          borderRadius: "999px",
                          background: "rgba(139,22,52,0.12)",
                          color: "#8b1634",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "15px",
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        <i className="fa fa-times" />
                      </button>
                    ) : (
                      <i
                        className="fa fa-search"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "11px",
                          color: "#8b1634",
                          fontSize: "14px",
                          lineHeight: 1,
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    {showLabelCodeDropdown ? (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 6px)",
                          left: 0,
                          right: 0,
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          background: "#fff",
                          boxShadow: "0 10px 24px rgba(2,6,23,0.12)",
                          maxHeight: "220px",
                          overflowY: "auto",
                          zIndex: 20,
                        }}
                      >
                        {filteredLabelCodeOptions.length ? (
                          filteredLabelCodeOptions.map((entry) => (
                            <button
                              key={entry.labelCode}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setCreateForm((p) => ({ ...p, labelCode: entry.labelCode }));
                                setShowLabelCodeDropdown(false);
                              }}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                border: "none",
                                borderBottom: "1px solid #f1f5f9",
                                background: "transparent",
                                padding: "10px 12px",
                                fontSize: "13px",
                                fontFamily: "ui-monospace, monospace",
                                color: "#1e293b",
                                cursor: "pointer",
                              }}
                            >
                              {entry.labelCode}
                              {entry.assetName ? ` (${entry.assetName})` : ""}
                            </button>
                          ))
                        ) : (
                          <div style={{ padding: "10px 12px", color: "#94a3b8", fontSize: "13px" }}>No matching label code</div>
                        )}
                      </div>
                    ) : null}
                    <div style={{ marginTop: "6px", fontSize: "11px", color: "#64748b" }}>
                      {labelCodeOptions.length} label codes available
                    </div>
                  </div>
                  <div style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowIssueTypeDropdown((v) => !v);
                        setShowSeverityDropdown(false);
                      }}
                      onBlur={() => setTimeout(() => setShowIssueTypeDropdown(false), 120)}
                      style={{
                        width: "100%",
                        border: "1px solid #dbe3ef",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        fontSize: "14px",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        color: "#1e293b",
                      }}
                    >
                      <span>{issueTypeOptions.find((x) => x.value === createForm.issueType)?.label || "Fault"}</span>
                      <i className={`fa fa-chevron-${showIssueTypeDropdown ? "up" : "down"}`} style={{ fontSize: "12px", color: "#8b1634" }} />
                    </button>
                    {showIssueTypeDropdown ? (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 6px)",
                          left: 0,
                          right: 0,
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          background: "#fff",
                          boxShadow: "0 10px 24px rgba(2,6,23,0.12)",
                          zIndex: 18,
                          overflow: "hidden",
                        }}
                      >
                        {issueTypeOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setCreateForm((p) => ({ ...p, issueType: opt.value }));
                              setShowIssueTypeDropdown(false);
                            }}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              border: "none",
                              borderBottom: "1px solid #f1f5f9",
                              background: createForm.issueType === opt.value ? "rgba(139,22,52,0.08)" : "#fff",
                              color: createForm.issueType === opt.value ? "#8b1634" : "#1e293b",
                              padding: "10px 12px",
                              fontSize: "13px",
                              fontWeight: createForm.issueType === opt.value ? 600 : 500,
                              cursor: "pointer",
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSeverityDropdown((v) => !v);
                        setShowIssueTypeDropdown(false);
                      }}
                      onBlur={() => setTimeout(() => setShowSeverityDropdown(false), 120)}
                      style={{
                        width: "100%",
                        border: "1px solid #dbe3ef",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        fontSize: "14px",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        color: "#1e293b",
                      }}
                    >
                      <span>{severityOptions.find((x) => x.value === createForm.severity)?.label || "High"}</span>
                      <i className={`fa fa-chevron-${showSeverityDropdown ? "up" : "down"}`} style={{ fontSize: "12px", color: "#8b1634" }} />
                    </button>
                    {showSeverityDropdown ? (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 6px)",
                          left: 0,
                          right: 0,
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          background: "#fff",
                          boxShadow: "0 10px 24px rgba(2,6,23,0.12)",
                          zIndex: 17,
                          overflow: "hidden",
                        }}
                      >
                        {severityOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setCreateForm((p) => ({ ...p, severity: opt.value }));
                              setShowSeverityDropdown(false);
                            }}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              border: "none",
                              borderBottom: "1px solid #f1f5f9",
                              background: createForm.severity === opt.value ? "rgba(139,22,52,0.08)" : "#fff",
                              color: createForm.severity === opt.value ? "#8b1634" : "#1e293b",
                              padding: "10px 12px",
                              fontSize: "13px",
                              fontWeight: createForm.severity === opt.value ? 600 : 500,
                              cursor: "pointer",
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <textarea
                    placeholder="Description"
                    value={createForm.description}
                    onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                    rows={4}
                    style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", resize: "vertical" }}
                  />
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 pb-3 px-4 gap-2" style={{ background: "#fafafa" }}>
                <button className="btn btn-light border" style={{ color: "#4a5568", fontWeight: 600 }} onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button className="btn" style={{ background: "#8b1634", color: "#fff", fontWeight: 700 }} onClick={handleCreateTicket} disabled={createLoading}>
                  {createLoading ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const th = {
  textAlign: "left",
  fontSize: "13px",
  color: "#8b1634",
  padding: "12px",
  borderBottom: "1px solid #e2e8f0",
};

const td = {
  textAlign: "left",
  fontSize: "13px",
  color: "#2d3748",
  padding: "12px",
  borderBottom: "1px solid #f1f5f9",
};

const panelStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  background: "#fff",
  padding: "14px 16px",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
};

const detailLabelStyle = {
  fontSize: "11px",
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  paddingTop: "2px",
};

const detailValueStyle = {
  color: "#0f172a",
  fontWeight: 500,
};

const sectionSubtitleStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#475569",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
};

function detailStatusBadge(status) {
  const key = String(status || "").toLowerCase();
  const base = { display: "inline-block", padding: "5px 11px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, textTransform: "capitalize" };
  if (key === "closed") return { ...base, background: "#dcfce7", color: "#166534" };
  if (key === "in_progress") return { ...base, background: "#e0e7ff", color: "#3730a3" };
  if (key === "open") return { ...base, background: "rgba(139, 22, 52, 0.12)", color: "#8b1634", border: "1px solid rgba(139, 22, 52, 0.2)" };
  return { ...base, background: "#f1f5f9", color: "#475569" };
}

function statusBadge(status) {
  const key = String(status || "").toLowerCase();
  const base = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "92px", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, textTransform: "capitalize", whiteSpace: "nowrap", lineHeight: 1.2 };
  if (key === "closed") return { ...base, background: "#dcfce7", color: "#166534" };
  if (key === "in_progress") return { ...base, background: "#dbeafe", color: "#1d4ed8" };
  if (key === "open") return { ...base, background: "#fef3c7", color: "#92400e" };
  return { ...base, background: "#e2e8f0", color: "#334155" };
}
